<template lang="pug">
div
  //- Fetching
  template(v-if="isFetching && !initialized")
    v-skeleton-loader.py-8.px-4(type="list-item-three-line@5")
  template(v-else)
    //- Title and actions bars
    v-row.mt-6(no-gutters align="center")
      v-col
        .text-md.font-semibold Pre-orders
      v-col(align="end")
        .d-flex.align-center
          v-spacer
          v-btn(@click="clickSearch" icon color="neutral500")
            v-icon(size="20") $yzi-search-sm
          v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
    v-divider
    //- Filters
    .d-flex.align-center.my-2
      .flex-grow-1.d-flex
        filter-date.mr-2(ref="dateFilter" :filter.sync="dateFilter" title="Pre-order date")
        filter-payment-method.mr-2(ref="paymentMethodFilter" :filter.sync="paymentMethodFilter")
        filter-status.mr-2(ref="paymentStatusFilter" label="Payment status" :filter.sync="paymentStatusFilter" :statuses="paymentStatuses" multiple)
      v-btn.button-sm.underline.px-0(text @click="resetFilters" :disabled="allFilters.length === 0") Reset filters
    //- Data listing
    .data-table
      v-data-table.mt-sm-4.pb-6(:headers="headers" :items="items" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @update:sort-by="onSort" @update:sort-desc="onSort" @click:row="onViewPreorder" loader-height="2")
        template(v-slot:item.date="{ item }")
          .text-sm.neutral700--text {{ item.created | humanizeDate }}
        template(v-slot:item.item="{ item }")
          yz-truncated-list.my-3(:items="getItemNames(item)")
        template(v-slot:item.payment_status="{ item }")
          .d-flex.align-center
            v-icon.mr-2(:color="getPaymentStatusColor(item)" size="22") {{ getPaymentStatusIcon(item) }}
            .text-sm {{ getPaymentStatusLabel(item) }}
        template(v-slot:item.preorder_status="{ item }")
          y-chip(:label="getStatusLabel(item.status)" :color="getStatusChipColor(item.status)")
        template(v-slot:item.actions="{ item }")
          div(@click.stop)
            v-menu(offset-y left rounded="lg" attach)
              template(#activator="{ on }")
                v-btn(icon small v-on="on")
                  v-icon mdi-dots-horizontal
              v-list.py-0(style="border-radius: 8px;")
                v-list-item(@click="onViewPreorder(item)")
                  v-list-item-title.text-sm.basic800--text View pre-order
                v-divider
                v-list-item(@click="onCancelPreorder(item)")
                  v-list-item-title.text-sm.danger500--text Cancel pre-order
    div.d-flex.align-center.mb-4(v-if="totalCount > pageSize")
      v-spacer
      div
        yc-pagination-custom(v-model="page" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="fetchData")
  .dialogs
    cancel-reservation-preorder-dialog(v-if="showCancelDialog" v-model="showCancelDialog" type="preorder" :reservation-data="selectedItem" :loading="isCancelling" @confirm="onConfirmCancel" @refund="onProceedWithRefund")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import FilterDate from '@/components/module/filter/FilterDate.vue'
import FilterPaymentMethod from '@/components/module/filter/FilterPaymentMethod.vue'
import FilterStatus from '@/components/module/filter/FilterStatus.vue'
import CancelReservationPreorderDialog from '@/components/clinic/reservation-preorder/CancelReservationPreorderDialog.vue'

export default {
  components: { FilterDate, FilterPaymentMethod, FilterStatus, CancelReservationPreorderDialog },
  data () {
    return {
      isFetching: true,
      initialized: false,
      showSearchField: false,
      searchText: null,
      items: [],
      totalCount: 0,
      pageSize: 20,
      page: 1,
      sortBy: 'created',
      sortDesc: true,
      showCancelDialog: false,
      selectedItem: null,
      isCancelling: false,
      dateFilter: {},
      paymentMethodFilter: {},
      paymentStatusFilter: {},
      paymentStatuses: [
        { id: 'paid', name: 'Paid' },
        { id: 'unpaid', name: 'Unpaid' },
        { id: 'partially_refunded', name: 'Partially refunded' },
        { id: 'refunded', name: 'Refunded' }
      ]
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    patientId () {
      if (this.$route.params.id) return parseInt(this.$route.params.id)
      return null
    },
    headers () {
      return [
        { text: 'PRE-ORDER DATE', value: 'date', sortable: true, sort: () => 0 },
        { text: 'ITEMS', value: 'item', sortable: false },
        { text: 'PAYMENT STATUS', value: 'payment_status', sortable: false },
        { text: 'STATUS', value: 'preorder_status', sortable: true, sort: () => 0 },
        { text: '', value: 'actions', sortable: false }
      ]
    },
    ordering () {
      if (!this.sortBy) return '-id'
      const field = this.sortBy === 'date' ? 'created' : this.sortBy === 'preorder_status' ? 'status' : this.sortBy
      return this.sortDesc ? `-${field}` : field
    },
    allFilters () {
      const filters = []
      if (this.dateFilter.key) {
        if (this.dateFilter.key === 'between') {
          const values = this.dateFilter.value
          for (const i in values) { filters.push(values[i]) }
        } else { filters.push(this.dateFilter) }
      }
      if (this.paymentMethodFilter.key) { filters.push(this.paymentMethodFilter) }
      if (this.paymentStatusFilter.key) {
        filters.push({ key: 'payment_status', value: this.paymentStatusFilter.value })
      }
      return filters
    }
  },
  watch: {
    allFilters () {
      this.page = 1
      this.fetchData()
    }
  },
  mounted () {
    this.fetchData()
  },
  methods: {
    ...mapActions(['showMessage']),
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    queryChanged: debounce(function () {
      this.page = 1
      this.fetchData()
    }, 750),
    onSort () {
      this.$nextTick(() => {
        this.page = 1
        this.fetchData()
      })
    },
    resetFilters () {
      this.dateFilter = {}
      this.paymentMethodFilter = {}
      this.paymentStatusFilter = {}
    },
    async fetchData () {
      try {
        this.isFetching = true
        const params = {
          patient: this.patientId,
          page: this.page,
          page_size: this.pageSize,
          ordering: this.ordering
        }
        if (this.searchText) { params.search = this.searchText }
        this.allFilters.forEach(filter => {
          params[filter.key] = filter.value
        })
        const { data } = await this.$axios.get(`/clinics/${this.company.slug}/preorders`, { params })
        this.items = data.results
        this.totalCount = data.count
        this.initialized = true
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isFetching = false
      }
    },
    onViewPreorder (item) {
      this.$router.push({
        name: 'ClinicPreorderView',
        params: {
          preorderId: String(item.id),
          origin: 'ClinicPatientDetails',
          data: { params: { id: String(this.patientId) }, query: { tab: 'pre-orders' } }
        }
      })
    },
    onCancelPreorder (item) {
      this.selectedItem = item
      this.showCancelDialog = true
    },
    async onConfirmCancel () {
      this.isCancelling = true
      try {
        await this.$axios.post(`/clinics/${this.company.slug}/preorders/${this.selectedItem.id}/cancel`)
        this.showMessage(['success', 'Pre-order cancelled successfully'])
        this.showCancelDialog = false
        this.fetchData()
      } catch (err) {
        const message = err.response?.data?.detail || this.$t('error_message.unable_to_complete_request')
        this.showMessage(['error', message])
      } finally {
        this.isCancelling = false
      }
    },
    onProceedWithRefund () {
      this.showCancelDialog = false
    },
    getItemNames (item) {
      const products = item.products || item.lead?.products || []
      return products.map(p => p.name || p.product_name)
    },
    getPaymentStatusIcon (item) {
      if (item.payment_status === 'paid' || item.payment_status === 'refunded') {
        return '$yzi-check-circle-solid'
      }
      return '$yzi-alert-circle-solid'
    },
    getPaymentStatusColor (item) {
      if (item.payment_status === 'paid' || item.payment_status === 'refunded') {
        return 'success500'
      }
      return 'danger500'
    },
    getPaymentStatusLabel (item) {
      const map = {
        paid: 'Paid',
        unpaid: 'Unpaid',
        partially_refunded: 'Partially refunded',
        refunded: 'Refunded'
      }
      return map[item.payment_status] || item.payment_status || 'N/A'
    },
    getStatusLabel (status) {
      const map = {
        active: 'Active',
        fulfilled: 'Fulfilled',
        cancelled: 'Cancelled'
      }
      return map[status] || status || '-'
    },
    getStatusChipColor (status) {
      const map = {
        active: 'green',
        fulfilled: 'blue',
        cancelled: 'red'
      }
      return map[status] || 'normal'
    }
  }
}
</script>

<style lang="scss" scoped>
.data-table ::v-deep .v-data-table__wrapper table tbody tr:hover {
  cursor: pointer;
  background: var(--v-neutral200) !important;
}
</style>
